import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../context/AuthContext';
import SaveToLibraryButton from '../../components/SaveToLibraryButton';
import LibraryFreeNote from '../../components/LibraryFreeNote';
import {
  Search, FileText, Download, Loader2, Info, BookMarked, X, FileSignature,
} from 'lucide-react';

const LOGO_URL = '/inasw-logo-2026.jpg';

const FormCard = ({ f, saved, onSaved }) => (
  <div className="bg-white rounded-xl border p-5 flex flex-col" data-testid={`swf-card-${f.form_id}`}>
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-primary/5 flex items-center justify-center flex-shrink-0"><FileSignature className="w-5 h-5 text-primary" /></div>
      <div>
        <h3 className="font-serif text-lg font-bold text-primary leading-snug">{f.title}</h3>
        <p className="text-xs text-secondary font-semibold uppercase">{f.category_label}</p>
      </div>
    </div>
    <p className="text-sm text-gray-600 mt-3 flex-1">{f.description}</p>
    <div className="flex flex-wrap gap-2 mt-4">
      <a href={`${API_URL}/social-work-forms/${f.form_id}/pdf`} target="_blank" rel="noreferrer">
        <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" data-testid={`swf-pdf-${f.form_id}`}>
          <Download className="w-3.5 h-3.5 mr-1" /> Print / Download
        </Button>
      </a>
      <SaveToLibraryButton
        saved={saved} onSaved={onSaved}
        item={{ item_type: 'social_work_form', ref_id: f.form_id, title: f.title, subtitle: f.abbr, category: f.category_label, url: `${API_URL}/social-work-forms/${f.form_id}/pdf` }}
      />
    </div>
  </div>
);

const SocialWorkFormsDirectory = () => {
  const { isAuthenticated } = useAuth();
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [forms, setForms] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savedRefs, setSavedRefs] = useState([]);

  useEffect(() => {
    document.title = 'Social Work Forms Center — Intake, Assessment & Case Management Templates | I Need A Social Worker';
    axios.get(`${API_URL}/social-work-forms/meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (q) p.set('q', q);
      if (category) p.set('category', category);
      const r = await axios.get(`${API_URL}/social-work-forms?${p.toString()}`);
      setForms(r.data.forms || []); setTotal(r.data.total || 0);
    } catch (e) { setForms([]); } finally { setLoading(false); }
  }, [q, category]);

  useEffect(() => { fetchList(); }, [fetchList]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API_URL}/library/saved-refs?item_type=social_work_form`).then((r) => setSavedRefs(r.data.refs || [])).catch(() => {});
  }, [isAuthenticated]);

  const onSaved = (refId) => setSavedRefs((prev) => prev.includes(refId) ? prev : [...prev, refId]);

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <img src={LOGO_URL} alt="INASW" className="w-16 h-16 rounded-full object-cover ring-2 ring-secondary mb-4" />
          <p className="text-secondary font-semibold uppercase tracking-widest text-xs flex items-center gap-2"><FileText className="w-4 h-4" /> Ready-to-Use Templates</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mt-1">Social Work <span className="text-secondary">Forms Center</span><sup className="text-secondary text-[0.4em] align-super font-semibold ml-0.5">™</sup></h1>
          <p className="mt-3 text-white/85 max-w-3xl">{meta?.intro}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="mb-3"><LibraryFreeNote variant="banner" /></div>
        <div className="bg-white rounded-2xl border p-4 shadow-lg flex flex-col md:flex-row gap-2" data-testid="swf-search">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchList()}
              placeholder="Search forms (e.g. intake, biopsychosocial, SOAP, safety plan, consent)…"
              className="w-full h-11 rounded-md pl-9 pr-3 text-sm border" data-testid="swf-search-input" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="h-11 rounded-md px-3 text-sm border md:w-64 bg-white" data-testid="swf-filter-category">
            <option value="">All categories</option>
            {(meta?.categories || []).map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-5">
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setCategory('')} className={`px-3 py-1.5 rounded-full text-sm border ${!category ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700'}`} data-testid="swf-cat-all">All</button>
          {(meta?.categories || []).map((c) => (
            <button key={c.slug} onClick={() => setCategory(c.slug)} className={`px-3 py-1.5 rounded-full text-sm border ${category === c.slug ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 hover:border-secondary'}`} data-testid={`swf-cat-${c.slug}`}>{c.label}</button>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-xl font-bold text-primary">{total} {total === 1 ? 'template' : 'templates'}</h2>
          <div className="flex items-center gap-3">
            {(q || category) && <button onClick={() => { setQ(''); setCategory(''); }} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1"><X className="w-3 h-3" /> Clear</button>}
            {isAuthenticated && <Link to="/my-library?tab=social_work_form" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="swf-my-library-link"><BookMarked className="w-4 h-4" /> My Library</Link>}
          </div>
        </div>
        {loading ? (
          <div className="text-center py-16 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : forms.length === 0 ? (
          <div className="bg-white rounded-xl border p-10 text-center">
            <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-600 font-medium">No templates match your search.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {forms.map((f) => <FormCard key={f.form_id} f={f} saved={savedRefs.includes(f.form_id)} onSaved={onSaved} />)}
          </div>
        )}
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3 text-sm text-blue-800" data-testid="swf-notice">
          <Info className="w-5 h-5 flex-shrink-0 text-blue-600" /><p>{meta?.notice}</p>
        </div>
      </section>
    </div>
  );
};

export default SocialWorkFormsDirectory;
